import type { BadgeProps } from '../../../../core'
import type { components } from '@joyful/openapi/types.gen'
import type { EnumValueMeta } from '@joyful/utils'
import { createEnumSchema } from '@joyful/utils'
import type { LucideIcon } from 'lucide-react'
import {
  ClockIcon,
  DollarSignIcon,
  HistoryIcon,
  PauseIcon,
  RadioIcon,
  ReceiptIcon,
  ShieldAlertIcon,
  UsersIcon,
} from 'lucide-react'
import type { ComponentType } from 'react'

interface EncounterEnrichmentStatusMetadata extends EnumValueMeta {
  badgeVariant: BadgeProps['variant']
  description: string
  icon: ComponentType<React.SVGProps<SVGSVGElement>>
}

export const EncounterEnrichmentStatusSchema = createEnumSchema<
  components['schemas']['EncounterEnrichmentStatus'],
  EncounterEnrichmentStatusMetadata
>([
  {
    badgeVariant: 'warning',
    description:
      'Claims that the payer has not responded to yet. Follow-up recommended.',
    icon: DollarSignIcon,
    title: 'Awaiting Payer Response',
    value: 'AWAITING_PAYER_RESPONSE',
  },
  {
    badgeVariant: 'warning',
    description:
      'Claims that the payer has not responded to yet. Follow-up recommended.',
    icon: DollarSignIcon,
    title: 'Awaiting Primary Payer Response',
    value: 'AWAITING_PRIMARY_PAYER_RESPONSE',
  },
  {
    badgeVariant: 'warning',
    description:
      'Claims that the payer has not responded to yet. Follow-up recommended.',
    icon: DollarSignIcon,
    title: 'Awaiting Secondary Payer Response',
    value: 'AWAITING_SECONDARY_PAYER_RESPONSE',
  },
  {
    badgeVariant: 'warning',
    description:
      'Claims that the payer has not responded to yet. Follow-up recommended.',
    icon: DollarSignIcon,
    title: 'Awaiting Tertiary Payer Response',
    value: 'AWAITING_TERTIARY_PAYER_RESPONSE',
  },
  {
    badgeVariant: 'error',
    description:
      'Claims that were rejected by the payer or Clearinghouse. These claims should be corrected and resubmitted ASAP.',
    icon: ShieldAlertIcon,
    title: 'Rejected',
    value: 'REJECTED',
  },
  {
    badgeVariant: 'error',
    description:
      'Encounters that have a denial that have not been worked by the practice yet. These claims should either be appealed or re-submitted.',
    icon: ShieldAlertIcon,
    title: 'Denied',
    value: 'DENIED',
  },
  {
    badgeVariant: 'info',
    description:
      "Encounters that don't yet have a claim associated. These claims should either be billed to the patient or payer ASAP.",
    icon: DollarSignIcon,
    title: 'Not Billed Yet',
    value: 'NOT_BILLED_YET',
  },
  {
    badgeVariant: 'info',
    description:
      'Encounters that have an outstanding responsibility that needs to be collected.',
    icon: DollarSignIcon,
    title: 'Outstanding',
    value: 'OUTSTANDING',
  },
  {
    badgeVariant: 'success',
    description: 'Encounters that have been paid.',
    icon: DollarSignIcon,
    title: 'Paid',
    value: 'PAID',
  },
  {
    badgeVariant: 'info',
    description:
      'Encounters that have an outstanding patient responsibility (co-pay, deductible, co-insurance, etc.) that needs to be collected.',
    icon: UsersIcon,
    title: 'Patient Responsibility',
    value: 'PATIENT_RESPONSIBILITY',
  },
  {
    badgeVariant: 'neutral',
    description:
      'Encounters that have been written-off or voided. These may potentially be recoverable.',
    icon: ReceiptIcon,
    title: 'Write Off',
    value: 'WRITE_OFF',
  },
])

export const OverridableEncounterEnrichmentStatusSchema = createEnumSchema(
  EncounterEnrichmentStatusSchema.definitions.filter(
    (
      definition,
    ): definition is Exclude<
      typeof definition,
      { value: 'NOT_BILLED_YET' | 'OUTSTANDING' }
    > => {
      return (
        definition.value !== 'NOT_BILLED_YET' &&
        definition.value !== 'OUTSTANDING'
      )
    },
  ),
)

interface EncounterSnoozeFilterMetadata extends EnumValueMeta {
  icon?: LucideIcon
}

// snooze status filter
export const EncounterSnoozeFilterSchema = createEnumSchema<
  components['schemas']['EncounterSnoozeFilter'],
  EncounterSnoozeFilterMetadata
>([
  {
    description: 'Encounters that are not currently snoozed.',
    title: 'Not snoozed',
    value: 'not_snoozed',
  },
  {
    description: 'Encounters snoozed until a specific date.',
    icon: ClockIcon,
    title: 'Snoozed',
    value: 'snoozed',
  },
  {
    description: 'Encounters paused with an indefinite snooze.',
    icon: PauseIcon,
    title: 'Paused / snoozed indefinitely',
    value: 'snoozed_indefinitely',
  },
])

// agent run status
export const AgentRunStatusSchema = createEnumSchema<
  components['schemas']['AgentRunStatus']
>([
  { title: 'Awaiting human input', value: 'awaiting_human' },
  { title: 'Agent running', value: 'running' },
  { title: 'Agent waiting', value: 'hibernating' },
  { title: 'Agent failed', value: 'failed' },
])

interface GoLiveCohortMetadata extends EnumValueMeta {
  icon: LucideIcon
}

export const GoLiveCohortSchema = createEnumSchema<
  components['schemas']['GoLiveCohort'],
  GoLiveCohortMetadata
>([
  {
    description: 'Latest claim submission was within 60 days of go-live.',
    icon: RadioIcon,
    title: 'Ongoing',
    value: 'ONGOING',
  },
  {
    description:
      'Latest claim submission was more than 60 days before go-live.',
    icon: HistoryIcon,
    title: 'Historical',
    value: 'HISTORICAL',
  },
])

// ingestion source
interface IngestionSourceTypeSchemaMetadata extends EnumValueMeta {
  info?: string
}

export const IngestionSourceTypeSchema = createEnumSchema<
  components['schemas']['IngestionSourceType'],
  IngestionSourceTypeSchemaMetadata
>([
  { title: 'AdvancedMD', value: 'ADVANCEDMD' },
  { title: 'Availity', value: 'AVAILITY' },
  { title: 'Candid', value: 'CANDID' },
  { title: 'Canvas', value: 'CANVAS' },
  { title: 'CentralReach', value: 'CENTRALREACH' },
  { title: 'CharmHealth', value: 'CHARMHEALTH' },
  { title: 'ClaimMD', value: 'CLAIMMD' },
  { title: 'CollaborateMD', value: 'COLLABORATEMD' },
  { title: 'EDI Insight', value: 'EDI_INSIGHT' },
  { title: 'ePACES', value: 'EPACES' },
  { title: 'Healthie', value: 'HEALTHIE' },
  { title: 'ICANotes', value: 'ICANOTES' },
  { title: 'Inovalon', value: 'INOVALON' },
  { title: 'InSync', value: 'INSYNC' },
  { title: 'MindWise', value: 'MINDWISE' },
  { title: 'Office Ally', value: 'OFFICE_ALLY' },
  { title: 'OptiMantra', value: 'OPTIMANTRA' },
  { title: 'Optum Pay', value: 'OPTUM_PAY' },
  { title: 'PracticeQ', value: 'PRACTICEQ' },
  { title: 'SimplePractice', value: 'SIMPLE_PRACTICE' },
  { title: 'Stedi', value: 'STEDI' },
  { title: 'Tebra', value: 'TEBRA' },
  { title: 'TherapyNotes', value: 'THERAPY_NOTES' },
  { title: 'TriZetto', value: 'TRIZETTO' },
  { title: 'UHC TrackIt', value: 'UHC_TRACKIT' },
  { title: 'Valant', value: 'VALANT' },
  {
    info: `This payment was logged in Joyful Health through the **💰 Found payment** action.`,
    title: 'Joyful Health',
    value: 'JOYFUL_HEALTH',
  },
  { title: 'Waystar', value: 'WAYSTAR' },
])

interface WorkBlockLaneKeyMetadata extends EnumValueMeta {
  description: string
}

export const WorkBlockLaneKeySchema = createEnumSchema<
  components['schemas']['WorkBlockLaneKey'],
  WorkBlockLaneKeyMetadata
>([
  {
    description: 'Which claims BPO billers work',
    title: 'BPO',
    value: 'bpo',
  },
  {
    description: 'Which claims RCM specialists work',
    title: 'RCM specialist',
    value: 'rcm_specialist',
  },
])

export const IngestionSourceDataTypeSchema = createEnumSchema<
  components['schemas']['IngestionSourceDataType']
>([
  { title: 'ERA', value: 'ERA' },
  { title: 'EOB', value: 'EOB' },
  { title: 'Verified', value: 'Scraped' },
  { title: 'Manual entry', value: 'Manual Entry' },
])
